import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import {
  InformacionEmpresaHg,
  InformacionEmpresaHgFiltro
} from 'src/app/domain/model/informacion-empresa/informacion-empresa-oc';
import { InformacionEmpresaRepository } from 'src/app/domain/service/informacion-empresa/informacion-empresa-repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';

interface OpcionOficina {
  label: string;
  value: number;
}

const MAX_EVIDENCIA_BYTES = 10 * 1024 * 1024;
const SAFE_FILENAME_FALLBACK = 'evidencia.bin';

@Component({
  selector: 'app-informacion-empresa-historial-gestiones',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    TableModule,
    DatePickerModule,
    DropdownModule,
    DialogModule,
    PaginatorModule,
    ToastModule,
    TooltipModule
  ],
  providers: [MessageService],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <div class="p-3 sm:p-6 max-w-6xl mx-auto">
      <p-toast position="top-right"></p-toast>

      <div class="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <span class="text-sm text-gray-600 mb-2 flex items-center gap-2"
            >Información Empresa >><b> Historial de Gestiones</b></span
          >
          <h1
            class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100"
          >
            Historial de Gestiones
          </h1>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Consulta de gestiones registradas
          </p>
        </div>
        <a routerLink="/informacion-empresa" [queryParams]="queryParamsRegreso">
          <button
            pButton
            type="button"
            label="Regresar"
            icon="pi pi-arrow-left"
            severity="secondary"
            class="p-button-outlined"
          >
            <span class="sr-only">Regresar</span>
          </button>
        </a>
      </div>

      <div
        class="rounded-lg border border-blue-200 bg-blue-100 p-4 text-blue-900 mb-4"
      >
        <div class="font-semibold mb-2">Contexto de consulta</div>
        <div class="text-sm grid grid-cols-1 gap-2">
          <div class="rounded px-3 py-2 border border-blue-100">
            <span class="font-medium">Cliente seleccionado:</span>
            <span class="ml-2">{{ nombreCliente() }}</span>
          </div>
          <div class="rounded px-3 py-2 border border-blue-100">
            <span class="font-medium">Consultor seleccionado:</span>
            <span class="ml-2">{{ nombreConsultor() }}</span>
          </div>
        </div>
      </div>

      <div
        class="border border-gray-200 dark:border-gray-700 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800"
      >
        <div class="font-medium text-gray-700 dark:text-gray-200 mb-3 text-sm">
          Filtros
        </div>

        <div class="grid grid-cols-1 gap-3">
          <div class="md:max-w-md">
            <label
              for="filtroOficina"
              class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
            >
              Oficina <span class="text-red-500">*</span>
            </label>
            <p-dropdown
              inputId="filtroOficina"
              [options]="oficinas()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filtroOficina"
              [filter]="true"
              [showClear]="false"
              [style]="{ width: '100%' }"
              appendTo="body"
            >
            </p-dropdown>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                for="fechaInicialLlamada"
                class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >
                Fecha Inicial Llamada <span class="text-red-500">*</span>
              </label>
              <p-datepicker
                inputId="fechaInicialLlamada"
                [(ngModel)]="fechaInicial"
                dateFormat="yy/mm/dd"
                [showIcon]="true"
                [style]="{ width: '100%' }"
                appendTo="body"
              >
              </p-datepicker>
            </div>

            <div>
              <label
                for="fechaFinalLlamada"
                class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >
                Fecha Final Llamada <span class="text-red-500">*</span>
              </label>
              <p-datepicker
                inputId="fechaFinalLlamada"
                [(ngModel)]="fechaFinal"
                dateFormat="yy/mm/dd"
                [showIcon]="true"
                [style]="{ width: '100%' }"
                appendTo="body"
              >
              </p-datepicker>
            </div>
          </div>
        </div>

        <div class="mt-4 flex flex-wrap justify-end sm:justify-start gap-3 w-full">
          <button
            pButton
            label="Limpiar"
            icon="pi pi-eraser"
            class="p-button-outlined"
            severity="secondary"
            (click)="limpiar()"
          >
            <span class="sr-only">Limpiar</span>
          </button>
          <button pButton label="Buscar" icon="pi pi-search" (click)="buscar()">
            <span class="sr-only">Buscar</span>
          </button>
        </div>
      </div>

      <div class="md:hidden space-y-3">
        @if (!loading() && items().length === 0) {
          <div class="text-center p-4 text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg">
            Búsqueda no arrojó resultado.
          </div>
        }

        @for (item of items(); track $index) {
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
            <div class="grid grid-cols-1 gap-2 text-sm">
              <div><span class="text-gray-500">Orientación:</span> <span class="text-gray-800 dark:text-gray-200">{{ fallback(item.orientacion, 'Sin orientación') }}</span></div>
              <div><span class="text-gray-500">Persona Ingresa:</span> <span class="text-gray-800 dark:text-gray-200">{{ fallback(item.personaIngresa, '-') }}</span></div>
              <div><span class="text-gray-500">Fecha Llamada:</span> <span class="text-gray-800 dark:text-gray-200">{{ item.fechaLlamada ? (item.fechaLlamada | date: 'yyyy/MM/dd HH:mm') : '-' }}</span></div>
              <div><span class="text-gray-500">Respuesta:</span> <span class="text-gray-800 dark:text-gray-200">{{ fallback(item.respuesta, '-') }}</span></div>
              <div><span class="text-gray-500">Motivo Reporte:</span> <span class="text-gray-800 dark:text-gray-200">{{ fallback(item.motivoReporte, 'Sin motivo') }}</span></div>
              <div><span class="text-gray-500">Persona Atendió:</span> <span class="text-gray-800 dark:text-gray-200">{{ fallback(item.personaAtendio, '-') }}</span></div>
            </div>
            <div class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p-button label="Observaciones" icon="pi pi-eye" severity="secondary" [outlined]="true" size="small" styleClass="w-full min-h-[44px]" [disabled]="!item.observaciones" (onClick)="abrirObservaciones(item)"></p-button>
              <p-button label="Evidencia" icon="pi pi-download" severity="secondary" [outlined]="true" size="small" styleClass="w-full min-h-[44px]" [disabled]="!item.dsevidencia || !item.dsevidenciaBase64" (onClick)="descargarEvidencia(item)"></p-button>
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
      <p-table
        [resizableColumns]="true"
        columnResizeMode="expand"
        [value]="items()"
        [loading]="loading()"
        responsiveLayout="scroll"
        styleClass="tabla-informacion-empresa p-datatable-sm p-datatable-striped p-datatable-gridlines" [scrollable]="true" [tableStyle]="{ 'min-width': '72rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th pResizableColumn><span class="grid-header-text" title="Orientación">Orientación</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Persona Ingresa">Persona Ingresa</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Fecha Llamada">Fecha Llamada</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Respuesta">Respuesta</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Motivo Reporte">Motivo Reporte</span></th>
            <th pResizableColumn><span class="grid-header-text" title="Persona Atendió">Persona Atendió</span></th>
            <th pResizableColumn class="text-center">Observaciones</th>
            <th pResizableColumn class="text-center">Evidencia</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-item>
          <tr>
            <td><span class="grid-cell-text" [attr.title]="fallback(item.orientacion, 'Sin orientación')">{{ fallback(item.orientacion, 'Sin orientación') }}</span></td>
            <td><span class="grid-cell-text" [attr.title]="fallback(item.personaIngresa, '-')">{{ fallback(item.personaIngresa, '-') }}</span></td>
            <td>
              {{
                item.fechaLlamada
                  ? (item.fechaLlamada | date: 'yyyy/MM/dd HH:mm')
                  : '-'
              }}
            </td>
            <td><span class="grid-cell-text" [attr.title]="fallback(item.respuesta, '-')">{{ fallback(item.respuesta, '-') }}</span></td>
            <td><span class="grid-cell-text" [attr.title]="fallback(item.motivoReporte, 'Sin motivo')">{{ fallback(item.motivoReporte, 'Sin motivo') }}</span></td>
            <td><span class="grid-cell-text" [attr.title]="fallback(item.personaAtendio, '-')">{{ fallback(item.personaAtendio, '-') }}</span></td>
            <td class="text-center">
              <button
                pButton
                icon="pi pi-eye"
                class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Ver observaciones"
                [disabled]="!item.observaciones"
                (click)="abrirObservaciones(item)"
              >
                <span class="sr-only">Ver observaciones</span>
              </button>
            </td>
            <td class="text-center">
              <button
                pButton
                icon="pi pi-download"
                class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Descargar evidencia"
                [disabled]="!item.dsevidencia || !item.dsevidenciaBase64"
                (click)="descargarEvidencia(item)"
              >
                <span class="sr-only">Descargar evidencia</span>
              </button>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="8" class="text-center p-6 text-gray-500">
              Búsqueda no arrojó resultado.
            </td>
          </tr>
        </ng-template>
      </p-table>
      </div>

      <div class="mt-4 flex justify-end">
        <p-paginator
          [rows]="rows()"
          [first]="first()"
          [totalRecords]="totalRecords()"
          [rowsPerPageOptions]="[10, 20, 50]"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
          dropdownAppendTo="body"
          appendTo="body"
          (onPageChange)="onPageChange($event)"
        >
        </p-paginator>
      </div>

      <p-dialog
        [(visible)]="mostrarDialogoObservaciones"
        [header]="'Observaciones'"
        [modal]="true"
        [style]="{ width: '95vw', maxWidth: '56rem' }"
        [breakpoints]="{ '960px': '96vw', '640px': '100vw' }"
      >
        <div
          class="rounded border border-gray-200 dark:border-gray-700 p-3 min-h-[220px] whitespace-pre-wrap break-words"
        >
          {{ observacionesSeleccionadas() || 'Sin datos' }}
        </div>
        <ng-template pTemplate="footer">
          <div class="flex flex-wrap justify-end sm:justify-start gap-3 w-full">
            <button
              pButton
              label="Cerrar"
              class="p-button-outlined"
              (click)="mostrarDialogoObservaciones = false"
            >
              <span class="sr-only">Cerrar</span>
            </button>
          </div>
        </ng-template>
      </p-dialog>
    </div>
  `
})
export class InformacionEmpresaHistorialGestionesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly informacionEmpresaRepository: InformacionEmpresaRepository =
    inject(InformacionEmpresaRepository);
  private readonly oficinaRepository: OficinaRepository =
    inject(OficinaRepository);
  private readonly usuarioState: UsuarioStateService =
    inject(UsuarioStateService);
  private readonly messageService = inject(MessageService);

  readonly items = signal<InformacionEmpresaHg[]>([]);
  readonly loading = signal(false);
  readonly totalRecords = signal(0);
  readonly first = signal(0);
  readonly rows = signal(10);
  readonly oficinas = signal<OpcionOficina[]>([]);
  fechaInicial: Date | null = null;
  fechaFinal: Date | null = null;
  filtroOficina: number | null = null;

  mostrarDialogoObservaciones = false;
  readonly observacionesSeleccionadas = signal('');

  private oficinaSesion: number | null = null;

  readonly nombreCliente = computed(
    () =>
      this.route.snapshot.queryParamMap.get('nombreBeneficiario') ??
      'Sin cliente'
  );
  readonly nombreConsultor = computed(
    () =>
      this.route.snapshot.queryParamMap.get('nombreConsultor') ??
      'Sin consultor'
  );
  readonly idBeneficiario = computed(() =>
    this.toNumber(
      this.route.snapshot.queryParamMap.get('nmconsecutivoBeneficiario')
    )
  );
  readonly idAsesor = computed(() => {
    return this.toNumber(
      this.route.snapshot.queryParamMap.get('nmconsecutivoConsultor')
    );
  });

  readonly resumenContexto = computed(() => {
    return `Cliente: ${this.nombreCliente()} | Consultor: ${this.nombreConsultor()}`;
  });

  readonly queryParamsRegreso = this.route.snapshot.queryParams;

  ngOnInit(): void {
    void this.inicializar();
  }

  private async inicializar(): Promise<void> {
    await this.precargarSesionYOficinas();
    const hoy = new Date();
    this.fechaFinal = new Date(
      hoy.getFullYear(),
      hoy.getMonth(),
      hoy.getDate()
    );
    this.fechaInicial = new Date(
      hoy.getFullYear(),
      hoy.getMonth(),
      hoy.getDate()
    );
    this.fechaInicial.setMonth(this.fechaInicial.getMonth() - 1);
    await this.buscar();
  }

  async buscar(): Promise<void> {
    if (!this.validarFiltros()) {
      return;
    }
    this.first.set(0);
    await this.consultar(0, this.rows());
  }

  async onPageChange(event: PaginatorState): Promise<void> {
    const first = event.first ?? this.first();
    const rows = event.rows ?? this.rows();
    this.first.set(first);
    this.rows.set(rows);

    if (!this.validarFiltros(false)) {
      return;
    }

    await this.consultar(first, rows);
  }

  limpiar(): void {
    this.items.set([]);
    this.totalRecords.set(0);
    this.first.set(0);
    this.fechaInicial = null;
    this.fechaFinal = null;
    this.filtroOficina = this.oficinaSesion;
    this.messageService.clear();
  }

  abrirObservaciones(item: InformacionEmpresaHg): void {
    this.observacionesSeleccionadas.set(item.observaciones ?? 'Sin datos');
    this.mostrarDialogoObservaciones = true;
  }

  descargarEvidencia(item: InformacionEmpresaHg): void {
    if (!item.dsevidencia || !item.dsevidenciaBase64) {
      this.mostrarMensaje(
        'warning',
        'La gestión no tiene evidencia disponible.'
      );
      return;
    }

    const estimatedSize = this.estimarTamanoBase64(item.dsevidenciaBase64);
    if (estimatedSize > MAX_EVIDENCIA_BYTES) {
      this.mostrarMensaje(
        'warning',
        'La evidencia supera el tamaño máximo permitido (10 MB).'
      );
      return;
    }

    try {
      const byteChars = atob(item.dsevidenciaBase64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i += 1) {
        byteNumbers[i] = byteChars.codePointAt(i) ?? 0;
      }
      const byteArray = new Uint8Array(byteNumbers);
      if (byteArray.byteLength > MAX_EVIDENCIA_BYTES) {
        this.mostrarMensaje(
          'warning',
          'La evidencia supera el tamaño máximo permitido (10 MB).'
        );
        return;
      }

      const blob = new Blob([byteArray], { type: 'application/octet-stream' });
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.sanitizarNombreArchivo(item.dsevidencia);
      link.click();
      globalThis.URL.revokeObjectURL(url);
    } catch {
      this.mostrarMensaje('error', 'No fue posible descargar la evidencia.');
    }
  }

  private estimarTamanoBase64(value: string): number {
    const sanitized = value.replace(/\s+/g, '');
    let padding = 0;
    if (sanitized.endsWith('==')) {
      padding = 2;
    } else if (sanitized.endsWith('=')) {
      padding = 1;
    }
    return Math.floor((sanitized.length * 3) / 4) - padding;
  }

  private sanitizarNombreArchivo(fileName: string): string {
    const cleaned = (fileName || '')
      .replace(/[^a-zA-Z0-9._ -]/g, '_')
      .replace(/\s+/g, ' ')
      .trim();

    const bounded = cleaned.slice(0, 120);
    const withoutLeadingDots = bounded.replace(/^[.]+/, '');
    if (
      !withoutLeadingDots ||
      withoutLeadingDots === '.' ||
      withoutLeadingDots === '..'
    ) {
      return SAFE_FILENAME_FALLBACK;
    }
    return withoutLeadingDots;
  }

  fallback(value: string | undefined, fallbackValue: string): string {
    const cleaned = (value ?? '').trim();
    return cleaned || fallbackValue;
  }

  private async precargarSesionYOficinas(): Promise<void> {
    const [usuario, oficinasData] = await Promise.all([
      this.usuarioState.cargar(),
      this.oficinaRepository.obtenerOficinas()
    ]);

    this.oficinas.set(
      oficinasData.map((oficina) => ({
        value: oficina.nmconsecutivo,
        label: oficina.dsoficina
      }))
    );

    const oficinaUsuario = usuario?.nmconsecOficina ?? null;
    this.oficinaSesion = oficinaUsuario;
    this.filtroOficina = oficinaUsuario;
  }

  private async consultar(first: number, rows: number): Promise<void> {
    this.loading.set(true);
    this.messageService.clear();

    const inudesde = first + 1;
    const inuhasta = first + rows;

    try {
      const filtro: InformacionEmpresaHgFiltro = {
        inuoficina: this.filtroOficina ?? undefined,
        inubeneficiario: this.idBeneficiario() ?? undefined,
        inuasesor: this.idAsesor() ?? undefined,
        idafeini: this.formatearFecha(this.fechaInicial),
        idafefin: this.formatearFecha(this.fechaFinal),
        inudesde,
        inuhasta
      };
      const resultado: InformacionEmpresaHg[] =
        await this.informacionEmpresaRepository.listarHistorialGestiones(
          filtro
        );

      this.items.set(resultado);
      this.totalRecords.set(resultado[0]?.totalRegistros ?? 0);

      if (resultado.length === 0) {
        this.mostrarMensaje('info', 'Búsqueda no arrojó resultado.');
      }
    } catch {
      this.items.set([]);
      this.totalRecords.set(0);
      this.mostrarMensaje('error', 'Error consultando historial de gestiones.');
    } finally {
      this.loading.set(false);
    }
  }

  private validarFiltros(mostrarError = true): boolean {
    if (!this.filtroOficina || !this.fechaInicial || !this.fechaFinal) {
      if (mostrarError) {
        this.mostrarMensaje(
          'warning',
          'Oficina, fecha inicial y fecha final son obligatorias.'
        );
      }
      return false;
    }

    if (this.fechaInicial.getTime() > this.fechaFinal.getTime()) {
      if (mostrarError) {
        this.mostrarMensaje(
          'warning',
          'La fecha inicial no puede ser mayor a la fecha final.'
        );
      }
      return false;
    }

    return true;
  }

  private mostrarMensaje(
    tipo: 'info' | 'warning' | 'error',
    texto: string
  ): void {
    const severityMap: Record<typeof tipo, 'info' | 'warn' | 'error'> = {
      info: 'info',
      warning: 'warn',
      error: 'error'
    };

    const summaryMap: Record<typeof tipo, string> = {
      info: 'Información',
      warning: 'Advertencia',
      error: 'Error'
    };

    this.messageService.add({
      severity: severityMap[tipo],
      summary: summaryMap[tipo],
      detail: texto
    });
  }

  private formatearFecha(value: Date | null): string | undefined {
    if (!value) {
      return undefined;
    }
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toNumber(value: string | null): number | null {
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
